import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import type { Call } from '@/shared/services/api/types';
import { applyServerCall, hangUp, syncActiveCalls } from '@/shared/services/calls/call-session';
import { onRealtimeConnect, onRealtimeEvent } from '@/shared/services/realtime/realtime-bus';
import { useCallStore } from '@/stores/call.store';
import { useIsSignedIn } from '@/stores/session.store';

import { OngoingCallPill } from '@/features/calls/components/OngoingCallPill';

/** Ce que portent `call:incoming` et `call:changed` : l'appel lui-même. */
type CallSignal = { call?: Call };

/**
 * Les appels, partout sur le site (le pendant du `CallProvider` du mobile).
 *
 * Écouter le serveur (une sonnerie, un changement d'état), relire les appels
 * en cours quand le canal revient ou que l'onglet redevient visible, ouvrir
 * l'écran d'appel quand ça sonne, et dire la sonnerie dans le titre de
 * l'onglet quand on regarde ailleurs. La pastille « appel en cours » vit ici.
 */
export function CallRoot() {
  const { t } = useTranslation('calls');
  const signedIn = useIsSignedIn();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const phase = useCallStore((state) => state.phase);
  const call = useCallStore((state) => state.call);
  // L'écran ne s'ouvre qu'une fois par appel : refermé pendant qu'il sonne,
  // il ne revient pas de force.
  const opened = useRef<string | null>(null);

  useEffect(() => {
    if (!signedIn) {
      return;
    }
    const apply = (signal: CallSignal) => {
      if (signal?.call) {
        applyServerCall(signal.call);
      }
    };
    const offIncoming = onRealtimeEvent<CallSignal>('call:incoming', apply);
    const offChanged = onRealtimeEvent<CallSignal>('call:changed', apply);
    const offConnect = onRealtimeConnect(() => void syncActiveCalls());
    void syncActiveCalls();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void syncActiveCalls();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      offIncoming();
      offChanged();
      offConnect();
      document.removeEventListener('visibilitychange', onVisible);
      // Sortie de session : l'appel ne survit pas au compte qui le passait.
      void hangUp();
    };
  }, [signedIn]);

  const callId = call?.id ?? null;
  useEffect(() => {
    if (
      phase === 'incoming' &&
      callId &&
      opened.current !== callId &&
      !pathname.startsWith('/call')
    ) {
      opened.current = callId;
      void navigate(`/call/${callId}`);
    }
  }, [phase, callId, pathname, navigate]);

  // L'onglet regardé ailleurs : son titre dit qu'un appel sonne.
  const ringingTitle =
    phase === 'incoming' && call
      ? t(call.kind === 'VIDEO' ? 'incoming.VIDEO' : 'incoming.AUDIO')
      : null;
  useEffect(() => {
    if (!ringingTitle) {
      return;
    }
    const previous = document.title;
    document.title = ringingTitle;
    return () => {
      document.title = previous;
    };
  }, [ringingTitle]);

  return <OngoingCallPill />;
}
